import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useI18n } from "../lib/i18n";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SponsorsBanner from "../components/SponsorsBanner";
import { SpotLoader } from "../components/Spot";
import { MapPin, Phone, Clock, Wifi, Calendar, Send, Mail } from "lucide-react";

const DAY_KEYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

function HoursList({ hours, t }) {
  if (!hours) return null;
  return (
    <div className="space-y-1.5 text-sm">
      {DAY_KEYS.map((k) => (
        <div key={k} className="flex justify-between gap-6">
          <span className="text-[#A8A39D] uppercase tracking-widest text-[11px] w-12">{t(`days.${k}`)}</span>
          <span className="text-[#F5F0EB]">{hours[k]?.trim() || t("cafe.closed")}</span>
        </div>
      ))}
    </div>
  );
}

function ReservationForm({ cafeId, t }) {
  const [form, setForm] = useState({
    customer_name: "", customer_phone: "", customer_email: "",
    party_size: 2, date: "", time: "", notes: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [err, setErr] = useState("");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setErr(""); setSuccess(false);
    try {
      await api.post("/reservations", { ...form, cafe_id: cafeId, customer_email: form.customer_email || undefined });
      setSuccess(true);
      setForm({ customer_name: "", customer_phone: "", customer_email: "", party_size: 2, date: "", time: "", notes: "" });
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div data-testid="reservation-success" className="border border-[#C5A059]/40 bg-[#C5A059]/5 p-8 text-center">
        <Calendar size={32} className="text-[#C5A059] mx-auto mb-4" />
        <div className="font-serif text-2xl mb-2">{t("cafe.success")}</div>
        <button onClick={() => setSuccess(false)} className="mt-4 text-xs uppercase tracking-widest text-[#C5A059] hover:text-[#D4AF37]">
          Nouvelle demande
        </button>
      </div>
    );
  }

  return (
    <form data-testid="reservation-form" onSubmit={submit} className="space-y-5">
      <div>
        <label className="overline text-xs">{t("cafe.yourName")}</label>
        <input data-testid="r-name" required value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)} className="luxe-input mt-2" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="overline text-xs">{t("cafe.yourPhone")}</label>
          <input data-testid="r-phone" required value={form.customer_phone} onChange={(e) => set("customer_phone", e.target.value)} className="luxe-input mt-2" />
        </div>
        <div>
          <label className="overline text-xs">{t("cafe.yourEmail")}</label>
          <input data-testid="r-email" type="email" value={form.customer_email} onChange={(e) => set("customer_email", e.target.value)} className="luxe-input mt-2" />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className="overline text-xs">{t("cafe.partySize")}</label>
          <input data-testid="r-party" type="number" min="1" max="50" required value={form.party_size} onChange={(e) => set("party_size", parseInt(e.target.value) || 1)} className="luxe-input mt-2" />
        </div>
        <div>
          <label className="overline text-xs">{t("cafe.date")}</label>
          <input data-testid="r-date" type="date" required value={form.date} onChange={(e) => set("date", e.target.value)} className="luxe-input mt-2" />
        </div>
        <div>
          <label className="overline text-xs">{t("cafe.time")}</label>
          <input data-testid="r-time" type="time" required value={form.time} onChange={(e) => set("time", e.target.value)} className="luxe-input mt-2" />
        </div>
      </div>
      <div>
        <label className="overline text-xs">{t("cafe.notes")}</label>
        <input data-testid="r-notes" value={form.notes} onChange={(e) => set("notes", e.target.value)} className="luxe-input mt-2" />
      </div>
      {err && <div className="text-red-400 text-sm">{err}</div>}
      <button data-testid="r-submit" type="submit" disabled={loading} className="btn-gold w-full">
        {loading ? "..." : <>{t("cafe.sendRequest")} <Send size={14} /></>}
      </button>
    </form>
  );
}

export default function CafePublic() {
  const { slug } = useParams();
  const { t, lang } = useI18n();
  const [cafe, setCafe] = useState(null);
  const [menu, setMenu] = useState([]);
  const [activeCategory, setActiveCategory] = useState("Tous");
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [c, m] = await Promise.all([
          api.get(`/cafes/${slug}`),
          api.get(`/cafes/${slug}/menu`),
        ]);
        setCafe(c.data); setMenu(m.data);
      } catch (e) {
        setError(formatApiError(e.response?.data?.detail));
      }
    })();
  }, [slug]);

  if (error) return (
    <div className="min-h-screen flex items-center justify-center text-[#A8A39D]"><div>{error}</div></div>
  );
  if (!cafe) return (
    <div className="min-h-screen flex items-center justify-center"><SpotLoader /></div>
  );

  const categories = menu.length ? ["Tous", ...Array.from(new Set(menu.map((p) => p.category)))] : [];
  const visibleMenu = activeCategory === "Tous" ? menu : menu.filter((p) => p.category === activeCategory);
  const fullAddress = [cafe.address, cafe.city, cafe.province, cafe.postal_code].filter(Boolean).join(", ");
  const mapsQuery = encodeURIComponent(fullAddress || cafe.name);

  return (
    <div data-testid="cafe-public" className="min-h-screen text-[#F5F0EB]">
      <Header />

      {/* Hero */}
      <section className="relative pt-28 pb-12">
        {cafe.banner_url && (
          <div className="absolute inset-0 opacity-50 bg-cover bg-center" style={{ backgroundImage: `url(${cafe.banner_url})` }} />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0908]/70 via-[#0A0908]/85 to-[#0A0908]" />
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 pt-16">
          <div className="overline mb-4">{cafe.category || "Commerce"} {cafe.city ? `· ${cafe.city}` : ""}</div>
          <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl font-light leading-[1]">{cafe.name}</h1>
          {(lang === "en" ? cafe.description_en : cafe.description) && (
            <p className="mt-8 text-[#A8A39D] max-w-2xl">{lang === "en" && cafe.description_en ? cafe.description_en : cafe.description}</p>
          )}
          <div className="mt-10 flex flex-wrap gap-3">
            {cafe.phone && (
              <a data-testid="call-btn" href={`tel:${cafe.phone}`} className="btn-ghost"><Phone size={14} /> {t("cafe.call")}</a>
            )}
            {fullAddress && (
              <a data-testid="directions-btn" href={`https://www.google.com/maps/dir/?api=1&destination=${mapsQuery}`} target="_blank" rel="noopener noreferrer" className="btn-ghost">
                <MapPin size={14} /> {t("cafe.directions")}
              </a>
            )}
            {cafe.accepts_reservations && (
              <a data-testid="goto-reserve" href="#reserve" className="btn-gold"><Calendar size={14} /> {t("cafe.reserve")}</a>
            )}
          </div>
        </div>
      </section>

      {/* Info grid: Address + Hours + Services */}
      <section className="px-6 md:px-12 py-16 border-t border-white/5">
        <div className="max-w-[1400px] mx-auto grid md:grid-cols-3 gap-px bg-white/5">
          <div className="bg-[#0E0C0B] p-8">
            <div className="overline mb-4 flex items-center gap-2"><MapPin size={12} /> {t("cafe.address")}</div>
            {fullAddress ? (
              <>
                <div className="font-serif text-xl mb-2">{cafe.address}</div>
                <div className="text-[#A8A39D] text-sm">{[cafe.city, cafe.province, cafe.postal_code].filter(Boolean).join(" ")}</div>
              </>
            ) : <div className="text-[#A8A39D] text-sm">—</div>}
          </div>
          <div className="bg-[#0E0C0B] p-8">
            <div className="overline mb-4 flex items-center gap-2"><Clock size={12} /> {t("cafe.hours")}</div>
            <HoursList hours={cafe.hours} t={t} />
          </div>
          <div className="bg-[#0E0C0B] p-8">
            <div className="overline mb-4 flex items-center gap-2"><Wifi size={12} /> {t("cafe.services")}</div>
            {cafe.services?.length ? (
              <div className="flex flex-wrap gap-2">
                {cafe.services.map((s) => (
                  <span key={s} className="px-3 py-1 border border-[#C5A059]/30 text-xs tracking-widest uppercase text-[#C5A059]">{s}</span>
                ))}
              </div>
            ) : <div className="text-[#A8A39D] text-sm">—</div>}
            {(cafe.phone || cafe.email) && (
              <div className="mt-6 pt-6 border-t border-white/5 space-y-2 text-sm">
                {cafe.phone && <div className="flex items-center gap-2 text-[#A8A39D]"><Phone size={12} className="text-[#C5A059]" /> {cafe.phone}</div>}
                {cafe.email && <div className="flex items-center gap-2 text-[#A8A39D]"><Mail size={12} className="text-[#C5A059]" /> {cafe.email}</div>}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Menu (display only) */}
      <section className="px-6 md:px-12 py-16 border-t border-white/5">
        <div className="max-w-[1400px] mx-auto">
          <div className="flex items-end justify-between mb-10">
            <div>
              <div className="overline mb-2">{t("cafe.menu")}</div>
              <h2 className="font-serif text-4xl font-light">Notre carte</h2>
            </div>
          </div>
          {!menu.length ? (
            <div className="text-[#A8A39D] py-12 text-center border border-white/5">{t("cafe.emptyMenu")}</div>
          ) : (
            <>
              <div className="flex items-center gap-2 overflow-x-auto pb-6">
                {categories.map((cat) => (
                  <button key={cat} data-testid={`cat-${cat}`} onClick={() => setActiveCategory(cat)} className={`px-5 py-2 text-xs tracking-[0.2em] uppercase whitespace-nowrap transition-all duration-300 ${activeCategory === cat ? "bg-[#C5A059] text-[#0A0908]" : "text-[#A8A39D] hover:text-[#F5F0EB] border border-white/10"}`}>{cat}</button>
                ))}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {visibleMenu.map((p, i) => (
                  <div key={p.id} data-testid={`menu-${p.id}`} className="luxe-card group animate-fade-up" style={{ animationDelay: `${i * 0.04}s` }}>
                    {p.image_url && (
                      <div className="aspect-[4/3] overflow-hidden bg-[#141210]">
                        <img src={p.image_url} alt={p.name} className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110" />
                      </div>
                    )}
                    <div className="p-6">
                      <div className="overline text-xs mb-2">{p.category}</div>
                      <div className="flex items-start justify-between gap-4">
                        <div className="font-serif text-xl">{lang === "en" && p.name_en ? p.name_en : p.name}</div>
                        <div className="font-serif text-lg text-[#C5A059] whitespace-nowrap">{p.price.toFixed(2)} $</div>
                      </div>
                      {(lang === "en" ? p.description_en : p.description) && (
                        <p className="text-[#A8A39D] text-sm mt-2 leading-relaxed">{lang === "en" && p.description_en ? p.description_en : p.description}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* Reservation */}
      <section id="reserve" className="px-6 md:px-12 py-20 border-t border-white/5 bg-[#0E0C0B]">
        <div className="max-w-[800px] mx-auto">
          <div className="text-center mb-10">
            <div className="overline mb-3">{t("cafe.reserve")}</div>
            <h2 className="font-serif text-4xl md:text-5xl font-light">{t("cafe.reserveTitle")}</h2>
          </div>
          {cafe.accepts_reservations ? (
            <ReservationForm cafeId={cafe.id} t={t} />
          ) : (
            <div className="text-[#A8A39D] text-center py-8 border border-white/5">{t("cafe.notAcceptingReservations")}</div>
          )}
        </div>
      </section>

      <SponsorsBanner />
      <Footer />
    </div>
  );
}
